import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiStatus } from '../../components/ApiStatus';
import { ApiError, apiGet } from '../../lib/api';
import { ADMIN_NAV } from '../../nav';
import { Alert, Card, Money, Num, PageHeader, Stat, StatGrid } from '../../ui';

/**
 * The operator's front door, on a launch day.
 *
 * It used to carry no figures at all, on the argument that every number lives
 * on the screen that owns it. That held for ten invited creators. With a
 * hundred strangers arriving in two days the questions an operator has each
 * morning are the same six — how many came, how many cannot submit yet and
 * why, what is waiting on me, what is stuck — and making them open six
 * screens to ask is how a queue goes unread. Each tile here is the number the
 * linked screen shows at the top of itself (`GET /admin/overview` computes
 * them the same way), and each one is a link to that screen, so this is an
 * index and not a second copy.
 */
export interface OverviewResponse {
  asOf: string;
  creators: {
    total: number;
    signupsToday: number;
    needsReview: number;
    unconfirmedEmails: number;
    noConnection: number;
  };
  submissions: { waiting: number; today: number };
  heldEarnings: { count: number; amountCents: number };
  outbox: { queued: number; failed: number };
  /** Absent on an API that predates support tickets. */
  support?: { open: number; unread: number };
  config: {
    emailProvider: string | null;
    signupRequiresInvite: boolean;
    creatorAutoApprove: boolean;
    publicWebOrigin: string;
  };
}

export function AdminOverview(): React.JSX.Element {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const areas = ADMIN_NAV.filter((item) => item.path !== '/admin');

  const load = useCallback(async () => {
    try {
      setData(await apiGet<OverviewResponse>('/admin/overview'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the numbers.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Overview"
        lead="What happened today, what is waiting on you, and whether the thing running it is up."
        actions={
          <Link className="btn btn-primary" to="/admin/review">
            Open the review queue
          </Link>
        }
      />

      {error && (
        <Alert tone="critical" title="Could not load the numbers.">
          <span>{error}</span>
        </Alert>
      )}

      {data && (
        <>
          {data.config.emailProvider === null && (
            <Alert tone="waiting" title="No email provider is configured">
              <span>
                Confirmation emails are being written to the Outbox and not sent. Creators can
                sign in, connect accounts and join campaigns, but cannot submit clips or request
                payouts until their address is confirmed — {data.creators.unconfirmedEmails} of
                them cannot yet. Set <code className="chip quiet">EMAIL_PROVIDER=resend</code>{' '}
                and the key (docs/email.md), or copy each link out of the{' '}
                <Link to="/admin/outbox">Outbox</Link>.
              </span>
            </Alert>
          )}

          <StatGrid>
            <Stat
              label="Signups today"
              value={<Num value={data.creators.signupsToday} />}
              note={
                <Link to="/admin/creators?filter=NEWEST">
                  {data.creators.total} creators in all
                </Link>
              }
              emphasis={data.creators.signupsToday > 0}
            />
            <Stat
              label="Unconfirmed emails"
              value={<Num value={data.creators.unconfirmedEmails} />}
              note={
                <Link to="/admin/creators?filter=UNCONFIRMED">
                  Cannot submit or be paid until they confirm
                </Link>
              }
            />
            <Stat
              label="No proven account"
              value={<Num value={data.creators.noConnection} />}
              note={
                <Link to="/admin/creators?filter=NO_CONNECTION">
                  Signed up, nothing connected yet
                </Link>
              }
            />
            {(data.creators.needsReview > 0 || !data.config.creatorAutoApprove) && (
              <Stat
                label="Waiting for approval"
                value={<Num value={data.creators.needsReview} />}
                note={
                  <Link to="/admin/creators?filter=NEEDS_REVIEW">
                    {data.config.creatorAutoApprove
                      ? 'Parked before auto-approval was on'
                      : 'Nobody can submit until you approve them'}
                  </Link>
                }
                emphasis={data.creators.needsReview > 0}
              />
            )}
            <Stat
              label="Clips waiting for review"
              value={<Num value={data.submissions.waiting} />}
              note={<Link to="/admin/review">{data.submissions.today} submitted today</Link>}
              emphasis={data.submissions.waiting > 0}
            />
            <Stat
              label="Held earnings"
              value={<Money cents={data.heldEarnings.amountCents} />}
              note={
                <Link to="/admin/review">
                  {data.heldEarnings.count} stopped by a fraud rule
                </Link>
              }
              emphasis={data.heldEarnings.count > 0}
            />
            <Stat
              label="Support tickets"
              value={<Num value={data.support?.open ?? 0} />}
              note={
                <Link to="/admin/support?status=OPEN">
                  need a reply
                  {(data.support?.unread ?? 0) > 0 ? `, ${data.support!.unread} unread` : ''}
                </Link>
              }
              emphasis={(data.support?.open ?? 0) > 0}
            />
            <Stat
              label="Outbox"
              value={<Num value={data.outbox.queued} />}
              note={
                <Link to="/admin/outbox">
                  queued
                  {data.outbox.failed > 0 ? `, ${data.outbox.failed} failed` : ''}
                </Link>
              }
              emphasis={data.outbox.failed > 0}
            />
          </StatGrid>

          <p className="muted">
            Signup is {data.config.signupRequiresInvite ? 'invitation-only' : 'open to anybody'};
            a confirmed creator is{' '}
            {data.config.creatorAutoApprove ? 'approved automatically' : 'approved by you'}.
            Links point at {data.config.publicWebOrigin}.
          </p>
        </>
      )}

      <ApiStatus />

      <Card title="The areas" sub="Each one owns its own numbers; the tiles above are its front page.">
        <ul className="rows">
          {areas.map((item) => (
            <li key={item.path}>
              <div className="row-head">
                <span className="row-title">
                  <Link to={item.path}>{item.label}</Link>
                </span>
              </div>
              <span className="muted">{DESCRIPTIONS[item.path] ?? ''}</span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

const DESCRIPTIONS: Record<string, string> = {
  '/admin/campaigns':
    'Create and fund a campaign, move it through its statuses, and freeze the terms creators accept.',
  '/admin/outbox':
    'Every transactional email. While no provider is configured this is where the confirmation links are.',
  '/admin/users': 'Who has an account, whether they confirmed it, and when they last signed in.',
  '/admin/creators':
    'Every creator, newest first: search by name, email or handle; their connections and the evidence behind each.',
  '/admin/invitations': 'Issue a code. It is readable exactly once, on the row that creates it.',
  '/admin/review':
    'The content question, and the held earnings a fraud rule stopped. Both need a reason to decide.',
  '/admin/support':
    'Every ticket a creator has opened: reply, re-run the checks on a rejected clip or overturn the decision, resolve.',
  '/admin/payouts': 'Cut the batch, approve it, pay each one and record the reference.',
  '/admin/ledger': 'The trial balance. It sums to zero, or something is wrong with the money.',
  '/admin/reports':
    'Clips, creators, views and money across every campaign, over any range. The one screen that answers "how are we doing".',
};
